/**
 * HYB Shop Web Management Dashboard
 * Main application component with routing and authentication
 */

import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

// Components
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { ForceChangePasswordPage } from './pages/ForceChangePasswordPage';
import { Dashboard } from './components/Dashboard';

// Pages
import { OrderManagement } from './pages/OrderManagement';
import { ChatManagement } from './pages/ChatManagement';
import { AppSettings } from './pages/AppSettings';
import { EMSStickers } from './pages/EMSStickerPage';
import { UserManagement } from './pages/UserManagement';
import { Monitoring } from './pages/Monitoring';
import { QuerySystem } from './pages/QuerySystem';
import { AuditLogs } from './pages/AuditLogs';
import { PDPACompliance } from './pages/PDPACompliance';
import { ProductManagement } from './pages/ProductManagement';

// Styles
import './styles/globals.css';

// Create QueryClient for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <Router>
            <div className="min-h-screen bg-gray-50">
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/force-change-password" element={<ForceChangePasswordPage />} />

                {/* Protected Routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                >
                  {/* Default route - Order Management */}
                  <Route index element={<Navigate to="/orders" replace />} />

                  {/* Main sections */}
                  <Route path="orders" element={<OrderManagement />} />
                  <Route path="products" element={<ProductManagement />} />
                  <Route path="chat" element={<ChatManagement />} />
                  <Route path="monitoring" element={<Monitoring />} />
                  <Route path="settings" element={<AppSettings />} />
                  <Route path="stickers" element={<EMSStickers />} />
                  <Route path="query" element={<QuerySystem />} />
                  <Route path="users" element={<UserManagement />} />
                  <Route path="logs" element={<AuditLogs />} />
                  <Route path="pdpa-compliance" element={<PDPACompliance />} />
                </Route>

                {/* Catch all route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>

              {/* Toast notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                  success: {
                    duration: 3000,
                    iconTheme: {
                      primary: '#4ade80',
                      secondary: '#fff',
                    },
                  },
                  error: {
                    duration: 5000,
                    iconTheme: {
                      primary: '#ef4444',
                      secondary: '#fff',
                    },
                  },
                }}
              />
            </div>
          </Router>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
